import { ChevronDownIcon } from '@heroicons/react/16/solid';
import {
  formatPrevalenceClass,
  getPrevalenceVisualData,
  parsePrevalenceData,
  type PrevalenceEntry,
} from '@/utils/prevalence';

type PrevalenceBreakdownContentProps = {
  rowData: any;
  compact?: boolean;
};

function PrevalenceEntryList({
  typeEntries,
  type,
  compact,
}: {
  typeEntries: PrevalenceEntry[];
  type: string;
  compact: boolean;
}) {
  const itemClass = compact ? 'text-xs text-gray-700 mb-3' : 'text-sm text-gray-700 mb-4';
  const classLabelClass = compact ? 'text-gray-500 text-[0.65rem]' : 'text-gray-500 text-xs';

  return (
    <ul className="space-y-1 mb-0">
      {typeEntries.map((entry, index) => (
        <li key={`${type}-${entry.geography}-${index}`} className={itemClass}>
          <div className="font-medium">
            {entry.geography || 'Unknown region'} -{' '}
            <span className={classLabelClass}>{formatPrevalenceClass(entry.class || 'Unknown')}</span>
          </div>
          {(() => {
            const visual = getPrevalenceVisualData(entry.class);
            if (!visual) return null;

            return (
              <div className="mt-1">
                <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-blue rounded-full h-1.5" style={{ width: `${visual.barPercent}%` }} />
                </div>
              </div>
            );
          })()}
        </li>
      ))}
    </ul>
  );
}

export function PrevalenceBreakdownContent({ rowData, compact = false }: PrevalenceBreakdownContentProps) {
  const entries = parsePrevalenceData({
    disease_prevalence_class: rowData?.disease_prevalence_class,
    disease_prevalence_geo_area: rowData?.disease_prevalence_geo_area,
    disease_prevalence_type: rowData?.disease_prevalence_type,
  });

  if (entries.length === 0) {
    return <p className="text-sm text-gray-400 text-center mt-2 mb-0">No prevalence data available.</p>;
  }

  const groupedByType = entries.reduce<Record<string, PrevalenceEntry[]>>((acc, entry) => {
    const key = entry.type || 'Unknown type';
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});

  const titleClass = compact
    ? 'text-xs font-semibold text-pink mb-0'
    : 'text-sm font-semibold text-pink mb-0';

  return (
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {Object.entries(groupedByType).map(([type, typeEntries]) => (
        <section key={type}>
          <details className="group">
            <summary className="cursor-pointer list-none flex items-center justify-between mb-2 focus:outline-none focus-visible:outline-none">
              <h3 className={titleClass}>{type}</h3>
              <ChevronDownIcon className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform" />
            </summary>
            <PrevalenceEntryList type={type} typeEntries={typeEntries} compact={compact} />
          </details>
        </section>
      ))}
    </div>
  );
}

export function hasPrevalenceBreakdownData(rowData: any): boolean {
  return (
    parsePrevalenceData({
      disease_prevalence_class: rowData?.disease_prevalence_class,
      disease_prevalence_geo_area: rowData?.disease_prevalence_geo_area,
      disease_prevalence_type: rowData?.disease_prevalence_type,
    }).length > 0
  );
}
